import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ArrowUpRight, Building2, Mail, ShieldCheck } from "lucide-react";

const FaqEnterprise = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "FAQ – Enterprise — FOZZIL";
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const appleEase = [0.16, 1, 0.3, 1];

  const enterpriseFaqs = [
    {
      q: "What level of enterprise integration does FOZZIL provide?",
      a: "FOZZIL provides complete lifecycle integration: from high-throughput REST and WebSocket APIs into your existing cloud infrastructure, to physically deploying the FOZZIL ARK on-premise edge appliance for zero-latency, air-gapped local intelligence."
    },
    {
      q: "What is FOZZIL ARK and how does it enable on-premise inference?",
      a: "FOZZIL ARK is our enterprise AI Edge-Server. It houses custom neural acceleration hardware designed to run T.A.E.L.S foundation models directly on your corporate local area network, ensuring zero outbound data transmission and zero reliance on external cloud availability."
    },
    {
      q: "Is FOZZIL MEDOS compliant with clinical hospital standards?",
      a: "Yes. FOZZIL MEDOS, powered by Zypher AI, is engineered under strict HIPAA, SOC2 Type II, and ISO 27001 compliance standards. Clinical data, patient charts, and diagnostic telemetry are processed client-side with zero telemetry leakage."
    },
    {
      q: "What is T.A.E.L.S Blacksite mode and how is proprietary IP secured?",
      a: "Blacksite operates in cryptographically isolated, air-gapped memory enclaves. Token sequences are masked with zero-knowledge homomorphic protocols, and volatile inference buffers are wiped upon session completion. Your source code and trade secrets are never used to train models."
    },
    {
      q: "Can T.A.E.L.S be deployed in private VPCs (AWS, GCP, Azure)?",
      a: "Yes. Enterprise customers can deploy containerized T.A.E.L.S reasoning nodes inside their private virtual clouds (AWS EKS, GCP GKE, Azure AKS) with custom ingress controls and hardware-level encryption key management (BYOK)."
    },
    {
      q: "What SLA guarantees and dedicated support are included?",
      a: "Enterprise contracts include 99.99% uptime SLAs, sub-100ms API inference latency targets, dedicated Technical Account Managers, custom red-teaming audits, and 24/7/365 priority escalation response."
    },
    {
      q: "How does enterprise volume pricing, seat provisioning, and licensing work?",
      a: "We offer tailored annual agreements with predictable per-seat or compute-unit billing, unified SAML/SSO directory sync (Okta, Azure AD, Google Workspace), and centralized administrative auditing."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white selection:bg-blue-600/30 selection:text-white font-sans antialiased overflow-x-hidden pt-28 md:pt-36 pb-24 px-6">
      {/* Subtle Atmospheric Ambient Glow */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-blue-600/[0.04] blur-[160px] rounded-full" />
        <div className="absolute bottom-1/3 left-1/4 w-[500px] h-[500px] bg-indigo-500/[0.03] blur-[140px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto space-y-16">
        {/* Answers & Specifications Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, ease: appleEase }}
          className="space-y-4 text-center max-w-2xl mx-auto"
        >
          <span className="text-xs font-mono text-blue-400 uppercase tracking-[0.25em]">
            Answers & Specifications
          </span>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-semibold text-white tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 font-light leading-relaxed">
            Enterprise Solutions · High-assurance intelligence, air-gapped enclaves, and on-premise infrastructure.
          </p>
        </motion.div>

        {/* Accordion Cards */}
        <div className="space-y-4">
          {enterpriseFaqs.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: idx * 0.05, ease: appleEase }}
                className="rounded-[24px] bg-white/[0.03] border border-white/[0.06] overflow-hidden transition-all duration-300 hover:border-blue-500/30"
              >
                <button
                  onClick={() => toggleFaq(idx)}
                  className="w-full flex items-center justify-between p-6 sm:p-7 text-left text-white font-medium text-base sm:text-lg cursor-pointer hover:bg-white/[0.02] transition-colors"
                >
                  <span className="pr-4">{faq.q}</span>
                  <div className="w-8 h-8 rounded-full bg-white/[0.05] flex items-center justify-center text-zinc-400 shrink-0">
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </div>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 sm:px-7 pb-6 text-sm sm:text-base text-zinc-400 font-light leading-relaxed border-t border-white/[0.04] pt-4">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Enterprise Support CTA Box */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: appleEase }}
          className="p-8 sm:p-10 rounded-[32px] bg-gradient-to-b from-white/[0.04] to-transparent border border-white/[0.08] text-center space-y-5"
        >
          <div className="w-12 h-12 mx-auto rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
            <Building2 className="w-6 h-6" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl sm:text-2xl font-semibold text-white">Need a custom enterprise deployment?</h3>
            <p className="text-sm sm:text-base text-zinc-400 font-light max-w-md mx-auto">
              Our solutions architects will design an air-gapped cluster or custom VPC integration aligned with your security compliance requirements.
            </p>
          </div>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <a
              href="mailto:enterprise@fozzil.in"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-semibold text-sm hover:bg-zinc-200 transition-all cursor-pointer shadow-lg"
            >
              <Mail className="w-4 h-4" />
              <span>Contact Enterprise Sales</span>
            </a>
            <a
              href="/security-and-privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/[0.05] hover:bg-white/[0.1] text-white border border-white/[0.1] font-medium text-sm transition-all cursor-pointer"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>Security Architecture</span>
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FaqEnterprise;